import { useState, useCallback, useEffect } from 'react';

import { useIzteachSessionStore } from '../../../stores/izteach/session';
import { useQltAuthStore } from '../../../stores/qlt/auth';
import type { EmbedAuthUser } from '../embed/types.ts';
import { getChatIdStorageKey, getSessionId } from '../utils/storage.ts';

/**
 * Hook tổng hợp thông tin người dùng xác thực và quản lý ID cuộc trò chuyện nháp
 * phục vụ việc nhúng iframe AI Quiz Supporter:
 * - Trích xuất thông tin giáo viên từ QLT Auth và IZTeach Session store
 * - Phân vùng lưu trữ draftChatId trong localStorage theo từng tài khoản
 * - Đảm bảo khách vãng lai nhận session ID duy nhất mỗi lần mở
 */
export function useQuizEmbedSession() {
  const [draftChatId, setDraftChatId] = useState<string | undefined>();

  const qltUser = useQltAuthStore((s) => s.user);
  const qltJwt = useQltAuthStore((s) => s.jwt);
  const izSession = useIzteachSessionStore();

  const teacherName = typeof qltUser?.teacher === 'object' ? qltUser?.teacher?.name : undefined;

  const authUser: EmbedAuthUser = {
    id: getSessionId(qltUser),
    name: teacherName || qltUser?.username || 'Giáo viên',
    email: qltUser?.email || '',
    avatar: undefined,
    vendorId: izSession.vendorId || undefined,
    accessToken: izSession.accessToken || qltJwt || undefined,
    role: qltUser?.role?.type || izSession.kind || 'teacher',
  };

  // Đồng bộ chat ID đã lưu khi tài khoản đăng nhập thay đổi
  useEffect(() => {
    const storageKey = getChatIdStorageKey(qltUser?.id ? String(qltUser.id) : undefined);
    const saved = localStorage.getItem(storageKey);
    setDraftChatId(saved || undefined);
  }, [qltUser?.id]);

  // Cập nhật chat ID mới vào state và lưu trữ cục bộ cho tài khoản đã đăng nhập
  const handleChatIdChange = useCallback(
    (chatId: string) => {
      setDraftChatId(chatId);
      if (qltUser?.id) {
        const storageKey = getChatIdStorageKey(String(qltUser.id));
        localStorage.setItem(storageKey, chatId);
      }
    },
    [qltUser?.id]
  );

  return {
    authUser,
    draftChatId,
    handleChatIdChange,
  };
}
